import {
  ECardinality,
  EMultiReportBlockMode,
  EMultiReportLayout,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IDisplayItem,
  IKType,
  IKTypeField,
  IMultiReportBlock,
  IMultiReportInstance,
  IMultiReportPrintData,
  IScreenInput,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  addMultiReportCollectionSource,
  createEmptyMultiReportInstance,
  removeMultiReportCollectionSource
} from "../../../../shared/utility/designer-action-actions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaRadioGroup from "../../../ui-kit/kuika-radio-group/kuika-radio-group";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { NavigationScreenInputPicker } from "./navigation-screen-input-picker";
import "./multi-report-print-symbols-group.scss";

interface MultiReportLayoutOption {
  label: string;
  value: EMultiReportLayout;
}

interface MultiReportModeOption {
  label: string;
  value: EMultiReportBlockMode;
}

const LAYOUT_OPTIONS: MultiReportLayoutOption[] = [
  { label: "Each report on a new page", value: "onePerPage" },
  { label: "Fit reports on the same page", value: "packed" }
];

const MODE_OPTIONS: MultiReportModeOption[] = [
  { label: "Fixed outputs", value: "static" },
  { label: "Per action element", value: "dynamic" }
];

/** The mode tabs' values in their on-screen order, so a mode's body slides in from its tab's side. */
const MODE_ORDER: string[] = MODE_OPTIONS.map((option: MultiReportModeOption): string => option.value);

export interface IMultiReportPrintSymbolsGroupState {}

export interface IMultiReportPrintSymbolsGroupProps {
  action: IAction;
  multiReportData?: IMultiReportPrintData;
  updateMultiReportData: (data: IMultiReportPrintData, changeReason?: ESdChangeReason) => void;
}

/**
 * Action panel UI for the GetMultipleReportsBase64 action. Lets the user pick one or more reports and the
 * merged-PDF page layout. Each report block is either:
 *  - "static": one or more output instances, each with its own report inputs; or
 *  - "dynamic": a bound collection (one render per element) whose report inputs map to the element's fields
 *    (current.field). Dynamic is restricted to action-result collections (see the backend resolver).
 */
class MultiReportPrintSymbolsGroup extends PureSdComponent<
  IMultiReportPrintSymbolsGroupProps & IWithSDProps,
  IMultiReportPrintSymbolsGroupState
> {
  constructor(props: IMultiReportPrintSymbolsGroupProps & IWithSDProps) {
    super(props);
    this.state = {};
  }

  getData = (): IMultiReportPrintData => {
    return this.props.multiReportData ?? { layoutMode: "onePerPage", reports: [] };
  };

  commit = (data: IMultiReportPrintData, changeReason?: ESdChangeReason): void => {
    this.props.updateMultiReportData(data, changeReason);
  };

  createEmptyInstance = (): IMultiReportInstance => {
    return createEmptyMultiReportInstance();
  };

  createEmptyBlock = (): IMultiReportBlock => {
    return { id: Guid.create().toString(), reportScreenId: "", mode: "static", instances: [] };
  };

  setLayout = (layoutMode: EMultiReportLayout): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    data.layoutMode = layoutMode;
    this.commit(data);
  };

  addReportBlock = (): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    data.reports.push(this.createEmptyBlock());
    this.commit(data);
  };

  removeReportBlock = (blockId: string): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    data.reports = data.reports.filter((block: IMultiReportBlock) => block.id !== blockId);
    this.commit(data);
  };

  setBlockReport = (blockId: string, reportScreenId: string): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    if (!block) return;
    const reportChanged: boolean = block.reportScreenId !== reportScreenId;
    block.reportScreenId = reportScreenId;
    // A different report has different inputs; reset to a single empty output so stale bindings don't linger.
    if (reportChanged || block.instances.length === 0) {
      block.instances = [this.createEmptyInstance()];
    }
    this.commit(data);
  };

  addInstance = (blockId: string): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    if (!block) return;
    block.instances.push(this.createEmptyInstance());
    this.commit(data);
  };

  removeInstance = (blockId: string, instanceId: string): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    if (!block) return;
    block.instances = block.instances.filter((instance: IMultiReportInstance) => instance.id !== instanceId);
    this.commit(data);
  };

  updateInstanceParams = (
    blockId: string,
    instanceId: string,
    params: ITargetScreenInputParam[],
    changeReason?: ESdChangeReason
  ): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    const instance: IMultiReportInstance | undefined = block?.instances.find(
      (i: IMultiReportInstance) => i.id === instanceId
    );
    if (!instance) return;
    instance.targetScreenInputParams = _.cloneDeep(params);
    this.commit(data, changeReason);
  };

  deleteInstanceParam = (blockId: string, instanceId: string, parameterName: string): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    const instance: IMultiReportInstance | undefined = block?.instances.find(
      (i: IMultiReportInstance) => i.id === instanceId
    );
    if (!instance) return;
    instance.targetScreenInputParams = instance.targetScreenInputParams.filter(
      (param: ITargetScreenInputParam) => param.name !== parameterName
    );
    this.commit(data);
  };

  setBlockMode = (blockId: string, mode: EMultiReportBlockMode): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    if (!block || block.mode === mode) return;
    block.mode = mode;
    // Switching mode changes how inputs are authored; reset to a single clean template and drop the
    // collection binding when leaving dynamic so stale state can't reach codegen.
    block.instances = [this.createEmptyInstance()];
    if (mode === "static") {
      block.dynamicSource = undefined;
    }
    this.commit(data);
  };

  // Collection-returning action definitions the user can add as a dynamic block's data source. The report
  // renders once per element of the chosen action's result list, so only Many-cardinality results qualify.
  getCollectionActionOptions = (): KuikaSelectboxOption[] => {
    return (this.props.sd.actionDefinitions ?? [])
      .filter((d: IActionDefinitionDto) => d.returnCardinality === ECardinality.Many && !!d.returnTypeModel)
      .slice()
      .sort((a: IActionDefinitionDto, b: IActionDefinitionDto) => a.name.localeCompare(b.name))
      .map((d: IActionDefinitionDto) => ({ value: d.id, label: d.name }));
  };

  // Adds the chosen collection query as a real sibling action in THIS action list, immediately before the
  // report action, and binds the dynamic block to its result. Both mutations are persisted in one atomic
  // setScreenDesign so the new action and the binding can never drift apart. Because the producing action then
  // runs earlier in the same list, its `result.X` is available to the report action's foreach — the codegen
  // resolves it exactly like a for-each collection (no in-flow error, no cross-scope parameter issues, and the
  // added action stays visible/editable in the normal actions list). "Same list" and not "same event" is what
  // makes a report action nested in an If/Then/Else branch (or For Each / Recurring / MQTT) work: codegen gives
  // each branch its own expression group, so the producer has to land in the branch, not above it.
  addCollectionActionAndBind = (blockId: string, actionDefinitionId: string): void => {
    const actionDef: IActionDefinitionDto | undefined = this.props.sd.actionDefinitions.find(
      (d: IActionDefinitionDto) => d.id === actionDefinitionId
    );
    if (!actionDef) return;

    const sd = this.props.sd.getScreenDesign();
    const di: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);
    const applied: boolean = addMultiReportCollectionSource(di, this.props.action.id, blockId, actionDef);
    if (!applied) return;

    this.props.sd.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
  };

  // Detaches the collection: removes the added query action from the list it was added to and clears the
  // block's binding, in one atomic update. Symmetric with add — the user added it here, so removing it here is
  // expected.
  removeCollectionAction = (blockId: string): void => {
    const sd = this.props.sd.getScreenDesign();
    const di: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);
    const applied: boolean = removeMultiReportCollectionSource(di, this.props.action.id, blockId);
    if (!applied) return;

    this.props.sd.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
  };

  getBoundCollectionLabel = (block: IMultiReportBlock): string => {
    const label: string | undefined = block.dynamicSource?.label;
    if (label && label.trim().length > 0) return label;
    const actionDef: IActionDefinitionDto | undefined = this.props.sd.actionDefinitions.find(
      (d: IActionDefinitionDto) => d.id === block.dynamicSource?.actionDefinitionId
    );
    return actionDef?.name ?? "Action";
  };

  // IKTypeField.type is typed IKType but serializes as a plain string at runtime (backend TypeElementModel
  // "type" is a string); the production symbol-picker reads it via toString(). Mirror that here.
  fieldTypeName = (field: IKTypeField | undefined): string => {
    const rawType: unknown = field?.type;
    if (typeof rawType === "string") return rawType;
    return (rawType as IKType | undefined)?.name ?? "";
  };

  // Resolves the element type's fields of the bound collection (mirrors the for-each "current row" lookup:
  // collection symbol -> action returnTypeModel -> element type -> fields).
  findChildTypeByName = (type: IKType | undefined, typeName: string, depth: number = 0): IKType | undefined => {
    if (!type?.elements || depth > 6) return undefined;
    const direct: IKTypeField | undefined = type.elements.find((f: IKTypeField) => this.fieldTypeName(f) === typeName);
    if (direct?.elementTypeModel) return direct.elementTypeModel;
    for (const field of type.elements) {
      const child: IKType | undefined = this.findChildTypeByName(field.elementTypeModel, typeName, depth + 1);
      if (child) return child;
    }
    return undefined;
  };

  getElementFields = (block: IMultiReportBlock): IKTypeField[] => {
    const actionDefinitionId: string | undefined = block.dynamicSource?.actionDefinitionId;
    if (!actionDefinitionId) return [];
    const actionDef: IActionDefinitionDto | undefined = this.props.sd.actionDefinitions.find(
      (x) => x.id === actionDefinitionId
    );
    const returnTypeModel: IKType | undefined = actionDef?.returnTypeModel;
    if (!returnTypeModel) return [];
    const targetType: string | undefined = block.dynamicSource?.targetType;
    let elementType: IKType | undefined = returnTypeModel;
    if (targetType && targetType !== returnTypeModel.name) {
      elementType = this.findChildTypeByName(returnTypeModel, targetType) ?? returnTypeModel;
    }
    return elementType?.elements ?? [];
  };

  getCurrentFieldBinding = (block: IMultiReportBlock, inputName: string): string | undefined => {
    const param: ITargetScreenInputParam | undefined = block.instances[0]?.targetScreenInputParams.find(
      (p: ITargetScreenInputParam) => p.name === inputName
    );
    return param?.symbol?.category === ESymbolCategory.current ? param.symbol.fieldName : undefined;
  };

  setInstanceCurrentField = (blockId: string, input: IScreenInput, field: IKTypeField | undefined): void => {
    const data: IMultiReportPrintData = _.cloneDeep(this.getData());
    const block: IMultiReportBlock | undefined = data.reports.find((b: IMultiReportBlock) => b.id === blockId);
    const instance: IMultiReportInstance | undefined = block?.instances[0];
    if (!instance) return;
    const params: ITargetScreenInputParam[] = instance.targetScreenInputParams.filter(
      (p: ITargetScreenInputParam) => p.name !== input.name
    );
    if (field) {
      const fieldType: string = this.fieldTypeName(field) || input.type;
      params.push({
        name: input.name,
        type: input.type,
        typeName: input.type,
        cardinality: input.cardinality,
        symbol: {
          category: ESymbolCategory.current,
          label: field.name,
          fieldName: field.name,
          currentType: fieldType,
          targetType: input.type,
          description: `Current.${field.name}`
        } as ISymbol
      } as ITargetScreenInputParam);
    }
    instance.targetScreenInputParams = params;
    this.commit(data);
  };

  getReportOptions = (): KuikaSelectboxOption[] => {
    return (this.props.sd.screenList ?? [])
      .filter((x) => x.publicMetaData.screenType === EScreenType.Report)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s) => ({ value: s.id, label: s.name }));
  };

  getFieldOptions = (fields: IKTypeField[]): KuikaSelectboxOption[] => {
    return fields.map((f: IKTypeField) => {
      const typeName: string = this.fieldTypeName(f);
      return { value: f.name, label: typeName ? `${f.name} · ${typeName}` : f.name };
    });
  };

  renderRemoveButton = (onClick: () => void, testId: string, label: string): JSX.Element => {
    return (
      <KuikaButton
        isIcon
        type="text"
        color="danger"
        size="sm"
        onClick={onClick}
        data-testid={testId}
        aria-label={label}
      >
        <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" />
      </KuikaButton>
    );
  };

  renderAddButton = (label: string, onClick: () => void, testId: string): JSX.Element => {
    return (
      <KuikaButton type="text" size="sm" onClick={onClick} className="kuika-multi-report__add" data-testid={testId}>
        <KuikaSvg name="plus" width={14} height={14} />
        <span>{label}</span>
      </KuikaButton>
    );
  };

  renderInstance = (block: IMultiReportBlock, instance: IMultiReportInstance, index: number): JSX.Element => {
    return (
      <div key={instance.id} className="kuika-multi-report__output">
        <div className="kuika-multi-report__output-header">
          <KuikaSmallMediumText>Output #{index + 1}</KuikaSmallMediumText>
          {block.instances.length > 1 &&
            this.renderRemoveButton(
              () => this.removeInstance(block.id, instance.id),
              `multiReportRemoveInstance_${index}`,
              "Remove output"
            )}
        </div>
        <NavigationScreenInputPicker
          action={this.props.action}
          targetScreenId={block.reportScreenId}
          targetScreenInputParams={instance.targetScreenInputParams}
          updateTargetScreenInputParams={(params: ITargetScreenInputParam[], changeReason?: ESdChangeReason) =>
            this.updateInstanceParams(block.id, instance.id, params, changeReason)
          }
          deleteTargetScreenInputParams={(parameterName: string) =>
            this.deleteInstanceParam(block.id, instance.id, parameterName)
          }
          screenType="Report"
        />
      </div>
    );
  };

  renderStaticOutputs = (block: IMultiReportBlock, index: number): JSX.Element => {
    return (
      <div className="kuika-multi-report__body">
        {block.instances.map((instance: IMultiReportInstance, instanceIndex: number) =>
          this.renderInstance(block, instance, instanceIndex)
        )}
        {this.renderAddButton("Add output", () => this.addInstance(block.id), `multiReportAddInstance_${index}`)}
      </div>
    );
  };

  renderDynamicBindings = (block: IMultiReportBlock): JSX.Element => {
    const hasCollection: boolean = !!block.dynamicSource?.actionDefinitionId;
    return (
      <div className="kuika-multi-report__body">
        <KuikaSmallMediumText className="kuika-multi-report__body-title">Action</KuikaSmallMediumText>
        {hasCollection ? this.renderBoundCollection(block) : this.renderCollectionActionPicker(block)}
        {hasCollection && this.renderFieldMapping(block)}
      </div>
    );
  };

  renderCollectionActionPicker = (block: IMultiReportBlock): JSX.Element => {
    return (
      <>
        <KuikaSelectbox
          placeholder="Add an action…"
          searchable
          value={undefined}
          options={this.getCollectionActionOptions()}
          onChange={(val: string | number) => this.addCollectionActionAndBind(block.id, val as string)}
          data-testid="multiReportAddCollectionAction"
        />
        <KuikaInfoBox
          color="gray"
          leftUnit={
            <span className="kuika-multi-report__notice">
              <KuikaSvg name="info-circle" width={16} height={16} color="var(--dark-7)" />
              <KuikaSmallText>
                Pick the action that returns your list. It&apos;s added to this event before the report action and runs
                once; the report is then rendered for each element.
              </KuikaSmallText>
            </span>
          }
        />
      </>
    );
  };

  renderBoundCollection = (block: IMultiReportBlock): JSX.Element => {
    return (
      <div className="kuika-multi-report__output">
        <div className="kuika-multi-report__output-header">
          <span className="kuika-multi-report__notice">
            <KuikaSvg name="layers" width={16} height={16} color="var(--dark-7)" />
            <KuikaSmallMediumText>{this.getBoundCollectionLabel(block)}</KuikaSmallMediumText>
          </span>
          {this.renderRemoveButton(
            () => this.removeCollectionAction(block.id),
            "multiReportRemoveCollection",
            "Remove action"
          )}
        </div>
        <KuikaSmallText className="kuika-multi-report__field-type">
          Added to this event · runs once, report rendered per element. Edit its parameters in the actions list.
        </KuikaSmallText>
      </div>
    );
  };

  renderFieldMapping = (block: IMultiReportBlock): JSX.Element | null => {
    const fields: IKTypeField[] = this.getElementFields(block);
    const reportInputs: IScreenInput[] = this.props.sd.getScreenInputsByScreenId(block.reportScreenId) ?? [];
    if (reportInputs.length === 0) return null;
    return (
      <div className="kuika-multi-report__body">
        <KuikaSmallMediumText className="kuika-multi-report__body-title">
          Map report inputs to element fields
        </KuikaSmallMediumText>
        {reportInputs.map((input: IScreenInput) => (
          // Key on the template instance id: rebinding the collection / switching mode mints a fresh instance
          // (new id), remounting these rows so KuikaSelectbox's internal value resets. Selecting a field
          // mutates the same instance in place (stable id → no remount mid-edit).
          <div key={`${input.name}-${block.instances[0]?.id ?? ""}`} className="kuika-multi-report__field-row">
            <KuikaSmallText className="kuika-multi-report__field-label">
              {input.name} <span className="kuika-multi-report__field-type">· {input.type}</span>
            </KuikaSmallText>
            <KuikaSelectbox
              placeholder="Element field…"
              searchable
              allowClear
              value={this.getCurrentFieldBinding(block, input.name)}
              options={this.getFieldOptions(fields)}
              onChange={(val: string | number | null) =>
                this.setInstanceCurrentField(
                  block.id,
                  input,
                  fields.find((f: IKTypeField) => f.name === val)
                )
              }
              data-testid={`multiReportDynamicField_${input.name}`}
            />
          </div>
        ))}
      </div>
    );
  };

  renderBlock = (block: IMultiReportBlock, index: number): JSX.Element => {
    const hasReport: boolean = !!block.reportScreenId && Guid.isGuid(block.reportScreenId);
    return (
      <div key={block.id} className="kuika-multi-report__block">
        <div className="kuika-multi-report__block-header">
          <KuikaTextMedium>
            Report <span className="kuika-multi-report__block-index">#{index + 1}</span>
          </KuikaTextMedium>
          {this.renderRemoveButton(
            () => this.removeReportBlock(block.id),
            `multiReportRemoveReport_${index}`,
            "Remove report"
          )}
        </div>

        <KuikaSelectbox
          placeholder="Select a report"
          searchable
          value={block.reportScreenId || undefined}
          options={this.getReportOptions()}
          onChange={(val: string | number) => this.setBlockReport(block.id, val as string)}
          data-testid={`multiReportSelectReportDropdown_${index}`}
        />

        {hasReport && (
          <>
            <KuikaTabs
              size="sm"
              value={block.mode}
              options={MODE_OPTIONS}
              onChange={(value: string) => this.setBlockMode(block.id, value as EMultiReportBlockMode)}
              dataTestId={`multiReportMode_${index}`}
            />
            <KuikaTabContent
              activeKey={block.mode === "dynamic" ? "dynamic" : "static"}
              order={MODE_ORDER}
              render={(mode: string): JSX.Element =>
                mode === "dynamic" ? this.renderDynamicBindings(block) : this.renderStaticOutputs(block, index)
              }
            />
          </>
        )}
      </div>
    );
  };

  render(): JSX.Element {
    const data: IMultiReportPrintData = this.getData();
    const hasReports: boolean = data.reports.length > 0;
    return (
      <div className="kuika-multi-report">
        <div className="kuika-multi-report__section">
          <KuikaTextMedium>Page layout</KuikaTextMedium>
          <KuikaRadioGroup
            options={LAYOUT_OPTIONS}
            value={data.layoutMode}
            onChange={(value: string | number) => this.setLayout(value as EMultiReportLayout)}
          />
        </div>

        <div className="kuika-multi-report__section">
          <div className="kuika-multi-report__section-header">
            <KuikaTextMedium>Reports</KuikaTextMedium>
            {hasReports && <KuikaBadge color="grey" content={data.reports.length} />}
          </div>

          {hasReports ? (
            <>
              <div className="kuika-multi-report__blocks">
                {data.reports.map((block: IMultiReportBlock, index: number) => this.renderBlock(block, index))}
              </div>
              {this.renderAddButton("Add report", this.addReportBlock, "multiReportAddReport")}
            </>
          ) : (
            <div className="kuika-multi-report__empty">
              <KuikaEmptyState
                svgName="layers"
                customName="No reports yet"
                description="Add a report to include in the merged PDF."
              />
              <KuikaButton type="secondary" size="sm" onClick={this.addReportBlock} data-testid="multiReportAddReport">
                <KuikaSvg name="plus" width={14} height={14} />
                <span>Add report</span>
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const multiReportPrintSymbolsGroup = withSD<IMultiReportPrintSymbolsGroupProps>(MultiReportPrintSymbolsGroup);
export { multiReportPrintSymbolsGroup as MultiReportPrintSymbolsGroup };
